'use client';

import { HStack, VStack, Box } from '@terra/ui-styled-system';
import { useEffect, useState } from 'react';
import { Text } from '../../components/text';
import { Badge } from '../../components/badge';
import { Tooltip } from '../../components/tooltip';
import { IconButton } from '../../components/icon-button';
import { TimelineDot } from './TimelineDot';
import { TimelineConnector } from './TimelineConnector';
import type {
  TimelineStepData,
  TimelineTimestampPlacement,
  TimelineDetailRenderer,
} from './types';
import { useDateFormatter } from '@terra/ui-formatters';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  LinkSquare02Icon,
} from '@terra/ui-icons';

type TimelineStepProps<TDetailData = unknown> = {
  step: TimelineStepData<TDetailData>;
  hideConnector?: boolean;
  dateFormatter?: {
    format: (date: Date) => string;
  };
  timestampPlacement?: TimelineTimestampPlacement;
  showOutsideTimestamp?: boolean;
  stepCollapsible?: boolean;
  detailRenderer?: TimelineDetailRenderer<TDetailData>;
};

export const TimelineStep = <TDetailData,>({
  step,
  hideConnector,
  dateFormatter: dateFormatterProp,
  timestampPlacement = 'inside',
  showOutsideTimestamp = true,
  stepCollapsible = false,
  detailRenderer,
}: TimelineStepProps<TDetailData>) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [pressedIndex, setPressedIndex] = useState<number | null>(null);
  const [shouldShowTooltip, setShouldShowTooltip] = useState<
    Record<number, boolean>
  >({});
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    if (pressedIndex === null) {
      return;
    }

    const clearPressedState = () => {
      setPressedIndex(null);
    };

    window.addEventListener('mouseup', clearPressedState);
    window.addEventListener('blur', clearPressedState);

    return () => {
      window.removeEventListener('mouseup', clearPressedState);
      window.removeEventListener('blur', clearPressedState);
    };
  }, [pressedIndex]);
  const defaultDateFormatter = useDateFormatter();
  const dateFormatter = dateFormatterProp || defaultDateFormatter;
  const formatTimelineTimestamp = ({
    timestamp,
  }: {
    timestamp: string | Date;
  }): string => {
    return dateFormatter.format(
      typeof timestamp === 'string' ? new Date(timestamp) : timestamp,
    );
  };

  const shouldRenderTitleAsText =
    typeof step.title === 'string' || typeof step.title === 'number';
  const hasStepDetails = Boolean(step.details?.length);
  const isStepCollapsible = stepCollapsible && hasStepDetails;
  const firstStepDetail = step.details?.[0];
  const shouldRenderOutsideTimestamp =
    timestampPlacement === 'outside' &&
    showOutsideTimestamp &&
    firstStepDetail?.timestamp != null;
  const shouldRenderInsideTimestamp =
    timestampPlacement === 'inside' ||
    (timestampPlacement === 'outside' && !showOutsideTimestamp);
  const outsideTimestamp =
    firstStepDetail?.timestamp != null
      ? formatTimelineTimestamp({
          timestamp: firstStepDetail.timestamp,
        })
      : null;
  const handleToggleExpanded = () => {
    if (!isStepCollapsible) {
      return;
    }

    setIsExpanded((currentValue) => !currentValue);
  };

  return (
    <HStack alignItems="stretch" w="full" gap={3}>
      {shouldRenderOutsideTimestamp && (
        <VStack alignItems="flex-end" flexShrink={0} minWidth="132px">
          <HStack
            height={8}
            alignItems="center"
            justifyContent="flex-end"
            w="full"
          >
            <Text
              fontSize="xs"
              fontWeight="regular"
              color="fg.neutral.muted"
              textAlign="right"
              w="full"
            >
              {outsideTimestamp}
            </Text>
          </HStack>
          <Box flex={1} />
        </VStack>
      )}
      <VStack alignItems="center" gap={0}>
        <TimelineDot icon={step.icon} tone={step.tone} />
        {!hideConnector && <TimelineConnector />}
      </VStack>
      <VStack flex={1} minWidth={0} alignItems="flex-start" w="full" pb={4}>
        <HStack alignItems="center" h={8} gap={1}>
          {shouldRenderTitleAsText ? (
            <Text fontSize="sm" fontWeight="medium">
              {step.title}
            </Text>
          ) : (
            <Box>{step.title}</Box>
          )}
          {isStepCollapsible ? (
            <IconButton
              size="xs"
              variant="ghost"
              onClick={handleToggleExpanded}
            >
              {isExpanded ? (
                <ArrowDown01Icon size={14} />
              ) : (
                <ArrowRight01Icon size={14} />
              )}
            </IconButton>
          ) : null}
        </HStack>
        {step.content ? (
          <Box w="full" pt={1} pb={hasStepDetails ? 2 : 0}>
            {step.content}
          </Box>
        ) : null}
        {hasStepDetails && (!isStepCollapsible || isExpanded) && (
          <VStack gap={2} w="full">
            {step.details?.map((detail, index) => {
              if (detailRenderer) {
                return (
                  <Box key={index} w="full">
                    {detailRenderer({
                      detail,
                      detailIndex: index,
                      step,
                    })}
                  </Box>
                );
              }

              const isInteractive = Boolean(detail.onClick);
              const isHovered = hoveredIndex === index;
              const isPressed = pressedIndex === index;
              const cardBorderColor = isPressed
                ? 'border.neutral.emphasized'
                : isHovered
                  ? 'border.neutral.focus'
                  : 'border.neutral.subtle';
              const cardBgColor =
                isPressed || isHovered
                  ? 'bg.neutral.subtleSoft'
                  : 'bg.neutral.light';
              const rightBadges = detail.rightBadges ?? [];
              const formattedTimestamp =
                detail.timestamp != null
                  ? formatTimelineTimestamp({ timestamp: detail.timestamp })
                  : null;
              const hasInsideTimestamp =
                shouldRenderInsideTimestamp && formattedTimestamp !== null;
              const hasDetailHeaderContent = Boolean(
                detail.leftBadge ||
                  detail.title ||
                  rightBadges.length > 0 ||
                  (timestampPlacement === 'outside' && detail.showLinkIcon),
              );
              const hasCardContent = Boolean(
                detail.description ||
                  hasDetailHeaderContent ||
                  hasInsideTimestamp ||
                  detail.showLinkIcon,
              );
              const shouldRenderDetailTitleAsText =
                typeof detail.title === 'string' ||
                typeof detail.title === 'number';
              const shouldRenderDetailDescriptionAsText =
                typeof detail.description === 'string' ||
                typeof detail.description === 'number';
              const descriptionLineClamp =
                detail.descriptionLineClamp === false
                  ? undefined
                  : (detail.descriptionLineClamp ?? 2);
              const handleMouseEnter = () => {
                if (isInteractive) {
                  setHoveredIndex(index);
                }
              };

              const handleMouseLeave = () => {
                if (isInteractive) {
                  setHoveredIndex(null);
                }
              };

              const handleMouseDown = () => {
                if (isInteractive) {
                  setPressedIndex(index);
                }
              };

              const handleMouseUp = () => {
                if (isInteractive) {
                  setPressedIndex(null);
                }
              };

              const handleClick = () => {
                if (detail.onClick) {
                  detail.onClick();
                }
              };

              const handleTooltipMouseEnter = (
                e: React.MouseEvent<HTMLDivElement>,
              ) => {
                const target = e.currentTarget;
                const textElement = target.querySelector('[data-truncate]');

                if (textElement) {
                  const isTruncated =
                    textElement.scrollWidth > textElement.clientWidth;

                  setShouldShowTooltip((prev) => ({
                    ...prev,
                    [index]: isTruncated,
                  }));
                }
              };

              const handleTooltipMouseLeave = () => {
                setShouldShowTooltip((prev) => ({
                  ...prev,
                  [index]: false,
                }));
              };

              if (!hasCardContent) {
                return null;
              }

              const tooltipContent = shouldShowTooltip[index]
                ? { customContent: detail.title }
                : detail.tooltip
                  ? { title: detail.tooltip }
                  : null;
              const cardContent = (
                <Box
                  w="full"
                  cursor={isInteractive ? 'pointer' : 'default'}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onClick={handleClick}
                  onContextMenu={detail.onContextMenu}
                  borderWidth="1px"
                  borderRadius="md"
                  borderStyle="solid"
                  borderColor={cardBorderColor}
                  bg={cardBgColor}
                >
                  <VStack py={3} px={2} gap={1} alignItems="flex-start">
                    {(hasInsideTimestamp || detail.showLinkIcon) && (
                      <HStack justifyContent="space-between" w="full">
                        {hasInsideTimestamp ? (
                          <Text
                            fontSize="xs"
                            fontWeight="regular"
                            color="fg.neutral.muted"
                          >
                            {formattedTimestamp}
                          </Text>
                        ) : null}
                        {detail.showLinkIcon && <LinkSquare02Icon size={16} />}
                      </HStack>
                    )}
                    {hasDetailHeaderContent && (
                      <HStack justifyContent="space-between" w="full" gap={2}>
                        <HStack
                          gap={2}
                          flex={1}
                          minWidth={0}
                          alignItems="center"
                        >
                          {detail.leftBadge && (
                            <Badge size="sm">{detail.leftBadge.label}</Badge>
                          )}
                          {detail.title && (
                            <Box
                              flex={1}
                              minWidth={0}
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                              data-truncate
                              onMouseEnter={handleTooltipMouseEnter}
                              onMouseLeave={handleTooltipMouseLeave}
                            >
                              {shouldRenderDetailTitleAsText ? (
                                <Text
                                  fontSize="sm"
                                  fontWeight={
                                    isPressed || isHovered
                                      ? 'medium'
                                      : 'regular'
                                  }
                                  color={
                                    isPressed || isHovered
                                      ? 'fg.neutral.default'
                                      : 'fg.neutral.muted'
                                  }
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  whiteSpace="nowrap"
                                >
                                  {detail.title}
                                </Text>
                              ) : (
                                <Box
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  whiteSpace="nowrap"
                                >
                                  {detail.title}
                                </Box>
                              )}
                            </Box>
                          )}
                        </HStack>
                        <HStack gap={2} flexShrink={0}>
                          {rightBadges.map((badge, badgeIndex) => (
                            <Badge key={badgeIndex} size="sm">
                              {badge.label}
                            </Badge>
                          ))}
                          {timestampPlacement === 'outside' &&
                            detail.showLinkIcon && (
                              <LinkSquare02Icon size={16} />
                            )}
                        </HStack>
                      </HStack>
                    )}
                    {detail.description &&
                      (shouldRenderDetailDescriptionAsText ? (
                        <Text
                          fontSize="sm"
                          fontWeight="regular"
                          color="fg.neutral.muted"
                          lineClamp={descriptionLineClamp}
                        >
                          {detail.description}
                        </Text>
                      ) : (
                        <Box
                          fontSize="sm"
                          fontWeight="regular"
                          color="fg.neutral.muted"
                          lineClamp={descriptionLineClamp}
                        >
                          {detail.description}
                        </Box>
                      ))}
                  </VStack>
                </Box>
              );
              const wrappedCardContent = tooltipContent ? (
                <Tooltip {...tooltipContent}>{cardContent}</Tooltip>
              ) : (
                cardContent
              );

              return (
                <Box key={index} w="full">
                  {wrappedCardContent}
                </Box>
              );
            })}
          </VStack>
        )}
      </VStack>
    </HStack>
  );
};
